import { Html, RoundedBoxGeometry } from '@react-three/drei';

import React from 'react';
import styled from 'styled-components';
import { getClubRecommendation } from '../utils/getClubRecommendation';
import type { HtmlProps } from '@react-three/drei/web/Html';
import { readableColor } from '@slope/uikit';

const ChipSignContainer = styled.div`
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  font-size: 1.2rem;
  font-family: 'Barlow', sans-serif;
  font-weight: 400;
  font-style: normal;
`;

const ChipSignMain = styled.div.withConfig({
  shouldForwardProp: (prop) => !['bg'].includes(prop),
  displayName: 'ChipSignMain',
})<{ bg?: React.CSSProperties['backgroundColor'] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3em;
  padding: 0.2em 0.5em;
  width: 3rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ bg = 'white' }) => readableColor(bg, 'black', 'white')};
  background-color: ${({ bg = 'white' }) => bg};
  transition:
    background-color,
    color 0.3s ease-in-out;
`;

const ChipSignAside = styled.div`
  display: flex;
  flex-direction: column;
  background: black;
  color: white;
  padding: 0.2em 0.5em;
  width: 2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipSignLabel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8em;
  color: white;
`;

export type ChipSignProps = HtmlProps & {
  /**
   * The distance from the chip to tee.
   */
  greenDistance: number;
  /**
   * The distance from the chip to tee.
   */
  teeDistance: number;
  /**
   * The color of the chip sign.
   */
  color?: string;
  /**
   * The size of the chip sign.
   */
  size?: number;
  /**
   * Whether the chip sign is displayed aside.
   */
  aside?: boolean;
};

export const ChipSign: React.FC<ChipSignProps> = ({
  color,
  aside = true,
  teeDistance,
  greenDistance,
  ...restProps
}) => {
  return (
    <Html
      {...restProps}
      geometry={<RoundedBoxGeometry args={[1, 0.5, 0.1]} radius={0.05} smoothness={4} />}
    >
      <ChipSignContainer>
        <ChipSignMain bg={color}>{teeDistance.toFixed(0)}</ChipSignMain>
        {aside && (
          <ChipSignAside>
            <ChipSignLabel>{getClubRecommendation(greenDistance).club}</ChipSignLabel>
            <ChipSignLabel>{greenDistance.toFixed(0)}</ChipSignLabel>
          </ChipSignAside>
        )}
      </ChipSignContainer>
    </Html>
  );
};
